import { useEffect } from 'react';

/**
 * Hook para gerenciar meta tags SEO dinamicamente
 * @param {Object} seoData - Dados de SEO
 * @param {string} seoData.title - Título da página
 * @param {string} seoData.description - Descrição da página
 * @param {string} seoData.canonical - URL canônica
 * @param {string} seoData.ogImage - URL da imagem para Open Graph
 * @param {string} seoData.ogType - Tipo Open Graph (article, website, etc)
 * @param {string} seoData.noindex - Se deve adicionar noindex (para 404, etc)
 */
const useSEO = ({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  noindex = false
}) => {
  useEffect(() => {
    const baseUrl = 'https://arukdog.com.br';
    const siteName = 'Aruk Dog';

    // Atualizar título
    if (title) {
      const fullTitle = title.includes('|') ? title : `${title} | ${siteName}`;
      document.title = fullTitle;

      // Atualizar meta property="og:title"
      updateOrCreateMeta('property', 'og:title', fullTitle);
      updateOrCreateMeta('name', 'twitter:title', fullTitle);
    }

    // Atualizar meta description
    if (description) {
      updateOrCreateMeta('name', 'description', description);
      updateOrCreateMeta('property', 'og:description', description);
      updateOrCreateMeta('name', 'twitter:description', description);
    }

    // Atualizar canonical URL
    if (canonical) {
      const canonicalUrl = canonical.startsWith('http') ? canonical : `${baseUrl}${canonical}`;
      updateOrCreateLink('rel', 'canonical', canonicalUrl);
    }

    // Atualizar Open Graph URL
    if (canonical) {
      const ogUrl = canonical.startsWith('http') ? canonical : `${baseUrl}${canonical}`;
      updateOrCreateMeta('property', 'og:url', ogUrl);
      updateOrCreateMeta('name', 'twitter:url', ogUrl);
    }

    // Atualizar Open Graph Image
    if (ogImage) {
      const imageUrl = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;
      updateOrCreateMeta('property', 'og:image', imageUrl);
      updateOrCreateMeta('name', 'twitter:image', imageUrl);
    }

    // Atualizar Open Graph Type
    if (ogType) {
      updateOrCreateMeta('property', 'og:type', ogType);
    }

    // Atualizar og:site_name (sempre manter)
    updateOrCreateMeta('property', 'og:site_name', siteName);

    // Meta robots (noindex)
    if (noindex) {
      updateOrCreateMeta('name', 'robots', 'noindex, nofollow');
    } else {
      // Remover noindex se existir
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta && robotsMeta.content === 'noindex, nofollow') {
        robotsMeta.remove();
      }
    }

    // Cleanup - restaurar valores padrão ao desmontar
    return () => {
      if (title) {
        document.title = siteName;
      }
    };
  }, [title, description, canonical, ogImage, ogType, noindex]);
};

/**
 * Atualiza ou cria uma meta tag
 */
const updateOrCreateMeta = (attribute, value, content) => {
  const selector = attribute === 'property' 
    ? `meta[property="${value}"]`
    : `meta[name="${value}"]`;
  
  let meta = document.querySelector(selector);
  
  if (!meta) {
    meta = document.createElement('meta');
    if (attribute === 'property') {
      meta.setAttribute('property', value);
    } else {
      meta.setAttribute('name', value);
    }
    document.head.appendChild(meta);
  }
  
  meta.setAttribute('content', content);
};

/**
 * Atualiza ou cria um link tag (para canonical)
 */
const updateOrCreateLink = (attribute, value, href) => {
  const selector = `link[${attribute}="${value}"]`;
  let link = document.querySelector(selector);
  
  if (!link) {
    link = document.createElement('link');
    link.setAttribute(attribute, value);
    document.head.appendChild(link);
  }
  
  link.setAttribute('href', href);
};

export default useSEO;
